/**
 * 시스템 리소스 모니터 컴포넌트
 * CPU, RAM, DISK, Network, Workers 실시간 모니터링
 * superuser 전용 - 오른쪽 하단 고정
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Progress, Card, Tooltip, Tabs } from 'antd';
import axios from '@/utils/axios';
import {
  DesktopOutlined,
  CloudServerOutlined,
  HddOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  TeamOutlined,
  MinusOutlined,
  CloseOutlined,
  ExpandOutlined,
} from '@ant-design/icons';

// JWT 토큰 만료 확인
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp;
    if (!exp) return false;
    // 만료 30초 전에 갱신
    return Date.now() >= (exp * 1000) - 30000;
  } catch {
    return true;
  }
};

// 토큰 갱신
const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return null;

    const response = await axios.post('/api/auth/refresh/', {
      refresh: refreshToken,
    });

    const newAccessToken = response.data.access;
    const newRefreshToken = response.data.refresh;

    localStorage.setItem('auth_token', newAccessToken);
    localStorage.setItem('access_token', newAccessToken);
    localStorage.setItem('token', newAccessToken); // 2026-11-03 token 키 동기화
    if (newRefreshToken) {
      localStorage.setItem('refresh_token', newRefreshToken);
    }

    console.log('[ResourceMonitor] 토큰 갱신 완료');
    return newAccessToken;
  } catch (error) {
    console.error('[ResourceMonitor] 토큰 갱신 실패:', error);
    return null;
  }
};

// 유효한 토큰 가져오기 (만료 시 자동 갱신)
const getValidToken = async (): Promise<string | null> => {
  let token = localStorage.getItem('auth_token') || localStorage.getItem('access_token') || localStorage.getItem('token');

  if (!token) return null;

  if (isTokenExpired(token)) {
    token = await refreshAccessToken();
  }

  return token;
};

interface SystemStats {
  cpu: number;
  ram: number;
  disk: number;
  upload: number;
  download: number;
  workers: {
    active: number;
    total: number;
  };
  detail?: {
    cpu_cores: number;
    cpu_freq_ghz: number;
    cpu_total_used: number;
    cpu_total_max: number;
    load_avg: number[];
    ram_used_gb: number;
    ram_total_gb: number;
    disk_used_gb: number;
    disk_total_gb: number;
  };
}

interface HistoryPoint {
  cpu: number;
  ram: number;
  timestamp: number;
}

const ResourceMonitor: React.FC = () => {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [minimized, setMinimized] = useState<boolean>(() => {
    const saved = localStorage.getItem('resourceMonitorMinimized');
    return saved === 'true';
  });
  const [visible, setVisible] = useState(true);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 색상 결정 함수
  const getProgressColor = (percent: number) => {
    if (percent >= 90) return '#ff4d4f';  // 빨강
    if (percent >= 80) return '#faad14';  // 노랑
    return '#52c41a';  // 초록
  };

  // 속도 포맷팅
  const formatSpeed = (mbps: number) => {
    if (mbps >= 1) {
      return `${mbps.toFixed(1)} MB/s`;
    }
    return `${(mbps * 1024).toFixed(0)} KB/s`;
  };

  // WebSocket 연결
  const connectWebSocket = useCallback(async () => {
    // 유효한 토큰 가져오기 (만료 시 자동 갱신)
    const token = await getValidToken();
    if (!token) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/ws/system-stats/?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[ResourceMonitor] WebSocket 연결됨');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'system_stats') {
            setStats(data);
            // 히스토리 추가 (최대 30개 = 1분)
            setHistory(prev => {
              const newHistory = [...prev, {
                cpu: data.cpu,
                ram: data.ram,
                timestamp: Date.now()
              }];
              return newHistory.slice(-30);
            });
          }
        } catch (e) {
          console.error('[ResourceMonitor] 메시지 파싱 오류:', e);
        }
      };

      ws.onclose = (event) => {
        console.log('[ResourceMonitor] WebSocket 연결 해제:', event.code);
        wsRef.current = null;
        // 권한 없음(4003)도 토큰 갱신 후 재시도
        if (visible) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, event.code === 4003 ? 1000 : 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('[ResourceMonitor] WebSocket 오류:', error);
      };

      wsRef.current = ws;
    } catch (e) {
      console.error('[ResourceMonitor] WebSocket 연결 실패:', e);
    }
  }, [visible]);

  // 탭 가시성 처리
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // 탭 숨김 - WebSocket pause
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ action: 'pause' }));
        }
      } else {
        // 탭 표시 - WebSocket resume
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ action: 'resume' }));
        } else {
          await connectWebSocket();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connectWebSocket]);

  // 초기 연결
  useEffect(() => {
    connectWebSocket();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket]);

  // 최소화 상태 저장
  useEffect(() => {
    localStorage.setItem('resourceMonitorMinimized', String(minimized));
  }, [minimized]);

  // 닫기 처리
  const handleClose = () => {
    setVisible(false);
    if (wsRef.current) {
      wsRef.current.close();
    }
  };

  // 스파크라인 SVG 생성
  const renderSparkline = () => {
    if (history.length < 2) return null;

    const width = 200;
    const height = 50;
    const paddingLeft = 22;
    const paddingRight = 2;
    const paddingY = 5;

    const chartWidth = width - paddingLeft - paddingRight;
    const chartHeight = height - paddingY * 2;

    const cpuPoints = history.map((h, i) => ({
      x: paddingLeft + (i / (history.length - 1)) * chartWidth,
      y: paddingY + (1 - h.cpu / 100) * chartHeight
    }));

    const ramPoints = history.map((h, i) => ({
      x: paddingLeft + (i / (history.length - 1)) * chartWidth,
      y: paddingY + (1 - h.ram / 100) * chartHeight
    }));

    const cpuPath = cpuPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    const ramPath = ramPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

    // Y축 기준선 위치
    const y0 = paddingY + chartHeight;        // 0%
    const y50 = paddingY + chartHeight * 0.5; // 50%
    const y100 = paddingY;                    // 100%

    return (
      <svg width={width} height={height} style={{ background: '#f5f5f5', borderRadius: 4 }}>
        {/* Y축 기준선 */}
        <line x1={paddingLeft} y1={y100} x2={width - paddingRight} y2={y100} stroke="#ddd" strokeWidth="0.5" strokeDasharray="2,2" />
        <line x1={paddingLeft} y1={y50} x2={width - paddingRight} y2={y50} stroke="#ddd" strokeWidth="0.5" strokeDasharray="2,2" />
        <line x1={paddingLeft} y1={y0} x2={width - paddingRight} y2={y0} stroke="#ddd" strokeWidth="0.5" />

        {/* Y축 레이블 */}
        <text x="2" y={y100 + 3} fontSize="8" fill="#999">100</text>
        <text x="6" y={y50 + 3} fontSize="8" fill="#999">50</text>
        <text x="12" y={y0 + 3} fontSize="8" fill="#999">0</text>

        {/* 데이터 라인 */}
        <path d={cpuPath} fill="none" stroke="#1890ff" strokeWidth="1.5" />
        <path d={ramPath} fill="none" stroke="#52c41a" strokeWidth="1.5" />
      </svg>
    );
  };

  if (!visible || !stats) return null;

  // 최소화 상태
  if (minimized) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000,
          cursor: 'pointer',
        }}
        onClick={() => setMinimized(false)}
      >
        <Tooltip title="리소스 모니터 열기">
          <div
            style={{
              background: stats.cpu >= 80 ? '#ff4d4f' : stats.cpu >= 60 ? '#faad14' : '#52c41a',
              color: 'white',
              padding: '8px 12px',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <DesktopOutlined />
            {stats.cpu.toFixed(0)}%
          </div>
        </Tooltip>
      </div>
    );
  }

  return (
    <Card
      size="small"
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#52c41a',
            animation: 'pulse 2s infinite'
          }} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>서버 상태</span>
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 8 }}>
          <MinusOutlined
            style={{ cursor: 'pointer', fontSize: 12 }}
            onClick={() => setMinimized(true)}
          />
          <CloseOutlined
            style={{ cursor: 'pointer', fontSize: 12 }}
            onClick={handleClose}
          />
        </div>
      }
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: 260,
        zIndex: 1000,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        borderRadius: 8,
      }}
      bodyStyle={{ padding: '8px 12px' }}
    >
      <Tabs
        size="small"
        defaultActiveKey="basic"
        items={[
          {
            key: 'basic',
            label: '기본',
            children: (
              <>
                {/* CPU */}
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: '#666' }}>
                      <DesktopOutlined style={{ marginRight: 4 }} />CPU
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: getProgressColor(stats.cpu) }}>
                      {stats.cpu.toFixed(1)}%
                    </span>
                  </div>
                  <Progress
                    percent={stats.cpu}
                    showInfo={false}
                    size="small"
                    strokeColor={getProgressColor(stats.cpu)}
                    trailColor="#f0f0f0"
                  />
                </div>

                {/* RAM */}
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: '#666' }}>
                      <CloudServerOutlined style={{ marginRight: 4 }} />RAM
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: getProgressColor(stats.ram) }}>
                      {stats.ram.toFixed(1)}%
                    </span>
                  </div>
                  <Progress
                    percent={stats.ram}
                    showInfo={false}
                    size="small"
                    strokeColor={getProgressColor(stats.ram)}
                    trailColor="#f0f0f0"
                  />
                </div>

                {/* DISK */}
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: '#666' }}>
                      <HddOutlined style={{ marginRight: 4 }} />DISK
                    </span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: getProgressColor(stats.disk) }}>
                      {stats.disk.toFixed(1)}%
                    </span>
                  </div>
                  <Progress
                    percent={stats.disk}
                    showInfo={false}
                    size="small"
                    strokeColor={getProgressColor(stats.disk)}
                    trailColor="#f0f0f0"
                  />
                </div>

                {/* Network */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: '#666',
                  marginBottom: 6,
                  padding: '4px 0',
                  borderTop: '1px solid #f0f0f0',
                }}>
                  <span>
                    <ArrowUpOutlined style={{ color: '#1890ff', marginRight: 2 }} />
                    {formatSpeed(stats.upload)}
                  </span>
                  <span>
                    <ArrowDownOutlined style={{ color: '#52c41a', marginRight: 2 }} />
                    {formatSpeed(stats.download)}
                  </span>
                </div>

                {/* Workers */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  marginBottom: 6,
                }}>
                  <span style={{ color: '#666' }}>
                    <TeamOutlined style={{ marginRight: 4 }} />워커
                  </span>
                  <span style={{
                    fontWeight: 600,
                    color: stats.workers.active < stats.workers.total * 0.5 ? '#ff4d4f' : '#52c41a'
                  }}>
                    {stats.workers.active}/{stats.workers.total} 활성
                  </span>
                </div>

                {/* Sparkline Chart */}
                <div style={{ marginTop: 4 }}>
                  <div style={{ fontSize: 10, color: '#999', marginBottom: 4, display: 'flex', gap: 12 }}>
                    <span><span style={{ color: '#1890ff' }}>━</span> CPU</span>
                    <span><span style={{ color: '#52c41a' }}>━</span> RAM</span>
                  </div>
                  {renderSparkline()}
                </div>
              </>
            ),
          },
          {
            key: 'detail',
            label: '상세',
            children: stats.detail ? (
              <div style={{ fontSize: 11 }}>
                {/* CPU 상세 */}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ color: '#666', marginBottom: 4, fontWeight: 600 }}>
                    <DesktopOutlined style={{ marginRight: 4 }} />CPU
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#333' }}>
                    <span>사용률</span>
                    <span style={{ fontWeight: 600, color: stats.detail.cpu_total_used > stats.detail.cpu_total_max * 0.8 ? '#ff4d4f' : '#333' }}>
                      {stats.detail.cpu_total_used}% / {stats.detail.cpu_total_max}%
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#333' }}>
                    <span>코어</span>
                    <span style={{ fontWeight: 600 }}>{stats.detail.cpu_cores}개</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#333' }}>
                    <span>클럭</span>
                    <span style={{ fontWeight: 600 }}>{stats.detail.cpu_freq_ghz} GHz</span>
                  </div>
                </div>

                {/* RAM 상세 */}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ color: '#666', marginBottom: 4, fontWeight: 600 }}>
                    <CloudServerOutlined style={{ marginRight: 4 }} />RAM
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#333' }}>
                    <span>사용량</span>
                    <span style={{ fontWeight: 600 }}>{stats.detail.ram_used_gb} / {stats.detail.ram_total_gb} GB</span>
                  </div>
                </div>

                {/* DISK 상세 */}
                <div style={{ marginBottom: 10 }}>
                  <div style={{ color: '#666', marginBottom: 4, fontWeight: 600 }}>
                    <HddOutlined style={{ marginRight: 4 }} />DISK
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#333' }}>
                    <span>사용량</span>
                    <span style={{ fontWeight: 600 }}>{stats.detail.disk_used_gb} / {stats.detail.disk_total_gb} GB</span>
                  </div>
                </div>

              </div>
            ) : (
              <div style={{ fontSize: 11, color: '#999', textAlign: 'center', padding: '20px 0' }}>
                로딩 중...
              </div>
            ),
          },
        ]}
      />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .ant-tabs-nav {
          margin-bottom: 8px !important;
        }
        .ant-tabs-tab {
          padding: 4px 8px !important;
          font-size: 11px !important;
        }
      `}</style>
    </Card>
  );
};

export default ResourceMonitor;
